import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiException } from '@/lib/api/types';
import { QUERY_DEFAULTS } from '@/lib/constants';
import { startUpdateWatch } from '@/lib/appUpdate';
import { UpdateBanner } from '@/components/UpdateBanner';
import App from '@/App';
import '@/index.css';

/**
 * 애플리케이션 진입점.
 *
 * 서버 상태는 TanStack Query가, 클라이언트 상태만 Zustand가 맡는다. (CLAUDE.md 5.4절)
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: QUERY_DEFAULTS.staleTime,
      gcTime: QUERY_DEFAULTS.gcTime,

      // 창을 다시 볼 때마다 재요청하지 않는다.
      // 사용자 1명 시스템이라 그 사이 데이터가 바뀌었을 가능성이 낮고,
      // 홈서버(8GB)에 불필요한 부하만 준다. 갱신이 필요한 화면은 개별로 켠다.
      refetchOnWindowFocus: false,

      retry: (failureCount, error) => {
        // 4xx는 재시도해도 결과가 같다. 요청 자체가 잘못된 것이다.
        // 네트워크 문제나 서버 5xx만 두 번까지 다시 시도한다.
        if (error instanceof ApiException && !error.isRetryable) {
          return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      // 변경 요청은 자동 재시도하지 않는다.
      // 멱등하지 않은 요청이 두 번 나가면 데이터가 중복될 수 있다.
      retry: false,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('#root 엘리먼트를 찾을 수 없다. index.html을 확인할 것.');
}

/*
  서비스 워커를 등록하고 업데이트 확인을 건다. (2026-08-30, docs/impl/89)

  🔴 React 밖에서 부른다
    화면이 그려지기 전에 등록돼야 첫 진입에서도 확인이 걸린다.
    등록 결과는 lib/appUpdate 가 들고 있다가 UpdateBanner 에 알린다.
*/
startUpdateWatch();

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {/* 새 버전 알림. 화면 위에 떠 있어야 해서 라우터 밖에 둔다 */}
      <UpdateBanner />
    </QueryClientProvider>
  </StrictMode>,
);
